import { z } from 'zod';
import { apiClient } from '../lib/api';
import { buildApiUrl } from '../utils/apiUrl';
import {
	ApiResponseSchema,
	PaginatedApiResponseSchema,
} from '../schemas';
import { UniversalChartDataSchema, NivoLineChartDataSchema } from '../schemas/chartSchema';
import { ChoroplethDataSchema } from '../schemas/chartSchema';

export const ProvinceSchema = z.object({
	maTinh: z.string(),
	tenTinh: z.string(),
	maTinhMoi: z.string().nullable().optional(),
	soTruong: z.number().int().nonnegative().optional(),
	soTruongTop149: z.number().int().nonnegative().optional(),
	createdAt: z.string(),
	updatedAt: z.string(),
	schools: z.array(z.object({
		maTruong: z.string(),
		tenTruong: z.string(),
		top149: z.boolean(),
		oldCodes: z.array(z.object({
			maTruong: z.string(),
			tenTruong: z.string(),
			maNoiBo: z.string().nullable()
		}).strip())
	}).strip()).optional(),
	mergedProvinces: z.array(z.object({
		maTinh: z.string(),
		tenTinh: z.string()
	}).strip()).optional()
}).strip();

export const CreateProvinceSchema = ProvinceSchema.pick({
	maTinh: true,
	tenTinh: true,
});

export const UpdateProvinceSchema = ProvinceSchema.pick({
	tenTinh: true,
	maTinhMoi: true,
});

export const ProvinceYearStatsSchema = z.object({
	maTinh: z.string(),
	tenTinh: z.string(),
	soTruong: z.number().optional(),
	yearData: z.record(z.string(), z.number())
}).strip();

export const ProvinceStatsResponseSchema = z.object({
	years: z.array(z.number()),
	stats: z.array(ProvinceYearStatsSchema)
}).strip();

export const ProvinceOverviewStatsSchema = z.object({
	totalProvinces: z.number(),
	provinceDetails: z.array(z.object({
		maTinh: z.string(),
		tenTinh: z.string(),
		studentCount: z.number(),
		schoolCount: z.number(),
		top149SchoolCount: z.number()
	}).strip())
}).strip();

export const provinceApi = {
	// Get all provinces with pagination support
	getProvinces: async (params?: URLSearchParams) => {
		const url = buildApiUrl('/provinces', params);
		const response = await apiClient.get(url);
		return PaginatedApiResponseSchema(ProvinceSchema.array()).parse(response.data);
	},

	// Get province by ID
	getProvinceById: async (id: string, includeSchools: boolean = false, merged: boolean = false) => {
		const params = new URLSearchParams();
		if (includeSchools) params.set('includeSchools', 'true');
		if (merged) params.set('merged', 'true');
		const url = buildApiUrl(`/provinces/${id}`, params.toString() ? params : undefined);
		const response = await apiClient.get(url);
		return ApiResponseSchema(ProvinceSchema).parse(response.data);
	},

	// CRUD operations for provinces
	createProvince: async (data: z.infer<typeof CreateProvinceSchema>) => {
		const response = await apiClient.post('/provinces', data);
		return ApiResponseSchema(ProvinceSchema).parse(response.data);
	},

	updateProvince: async (id: string, data: z.infer<typeof UpdateProvinceSchema>) => {
		const response = await apiClient.put(`/provinces/${id}`, data);
		return ApiResponseSchema(ProvinceSchema).parse(response.data);
	},

	deleteProvince: async (id: string): Promise<{ message: string }> => {
		const response = await apiClient.delete<{ message: string }>(`/provinces/${id}`);
		return response.data;
	},

	/** Danh mục tỉnh sau sáp nhập — nguồn hợp lệ cho ô chọn `maTinhMoi`. */
	getMergedCatalog: async () => {
		const response = await apiClient.get('/provinces/merged-catalog');
		return ApiResponseSchema(z.array(z.object({ maTinh: z.string(), tenTinh: z.string() }))).parse(
			response.data
		);
	},

	getProvinceStats: async (params?: URLSearchParams) => {
		const url = buildApiUrl('/provinces/stats/admission', params);
		const response = await apiClient.get(url);
		return PaginatedApiResponseSchema(ProvinceStatsResponseSchema).parse(response.data);
	},

	// Get province overview stats
	getProvinceOverview: async (params?: URLSearchParams) => {
		const url = buildApiUrl('/stats/provinces/overview', params);
		const response = await apiClient.get(url);
		return ApiResponseSchema(ProvinceOverviewStatsSchema).parse(response.data);
	},

	// Get province chart statistics
	getProvinceChartStats: async (params?: URLSearchParams) => {
		const url = buildApiUrl('/provinces/stats/chart-data', params);
		const response = await apiClient.get(url);
		return ApiResponseSchema(UniversalChartDataSchema).parse(response.data);
	},

	// Get Nivo line chart data for provinces
	getNivoLineChartData: async (params?: URLSearchParams) => {
		const url = buildApiUrl('/provinces/stats/nivo-chart-data', params);
		const response = await apiClient.get(url);
		return ApiResponseSchema(NivoLineChartDataSchema).parse(response.data);
	},

	// Get province choropleth data
	getProvinceChoroplethData: async (params?: URLSearchParams) => {
		const url = buildApiUrl('/provinces/stats/choropleth', params);
		const response = await apiClient.get(url);
		return ApiResponseSchema(ChoroplethDataSchema).parse(response.data);
	},

	// Get merged province choropleth data (after consolidation)
	getMergedProvinceChoroplethData: async (params?: URLSearchParams) => {
		const url = buildApiUrl('/provinces/stats/choropleth-merged', params);
		const response = await apiClient.get(url);
		return ApiResponseSchema(ChoroplethDataSchema).parse(response.data);
	},

	// Get merged province Nivo line chart data (after consolidation)
	getMergedNivoLineChartData: async (params?: URLSearchParams) => {
		const url = buildApiUrl('/provinces/stats/nivo-chart-data-merged', params);
		const response = await apiClient.get(url);
		return ApiResponseSchema(NivoLineChartDataSchema).parse(response.data);
	},

	// Get merged province stats (after consolidation) for DataGrid
	getMergedProvinceStats: async (params?: URLSearchParams) => {
		const url = buildApiUrl('/provinces/stats/admission-merged', params);
		const response = await apiClient.get(url);
		return PaginatedApiResponseSchema(ProvinceStatsResponseSchema).parse(response.data);
	},

	// Export provinces to Excel
	exportToExcel: async (params?: URLSearchParams): Promise<void> => {
		return apiClient.exportToExcel('/provinces/export/excel', params);
	},


	// Export merged provinces to Excel
	exportMergedToExcel: async (params?: URLSearchParams): Promise<void> => {
		return apiClient.exportToExcel('/provinces/merged/export/excel', params);
	},
};
